import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'antd';
import classNames from 'classnames';

import { FAVORITE_STATUS, HistoryListItem } from '@whelk/services/src/type/chat';
import {
  useDeleteHistory,
  useRenameHistory,
  useFavoriteHistory,
} from '@whelk/hooks/src/useHistoryList';
import usePageName from '@whelk/hooks/src/usePageName';
import { HISTORY_OPERATE_TYPE } from '@whelk/const/src/history';

import {
  HistoryItemMoreIcon,
  HistoryMenuDeleteIcon,
  HistoryMenuRenameIcon,
  HistoryMenuFavoriteIcon,
  HistoryMenuUnfavoriteIcon,
} from '@whelk/assets/src/web/history-icon';

import { trackOpClick } from '../track';

import './style.scss';

interface MenuItemProps {
  title: string;
  icon: React.ReactNode;
  type?: 'warning' | 'normal';
}

interface MenuDropdownProps {
  item: HistoryListItem;
  className?: string;
  historyItemClassName?: string;
  children?: React.ReactNode;
}

const MenuItem = ({ title, icon, type }: MenuItemProps) => (
  <div className={`flex cursor-pointer items-center px-2 py-2 text-[14px] font-medium  `}>
    <div
      className={classNames(
        'text-col_text00 relative flex w-full items-center gap-1 rounded-[8px] md:min-w-[108px]',
      )}
    >
      {icon && (
        <div
          className={`flex h-[28px] w-[28px] items-center justify-center ${
            type === 'warning' ? 'text-col_accent01' : ''
          }`}
        >
          {icon}
        </div>
      )}
      <div
        className={`flex items-center gap-1 text-[13px] ${
          type === 'warning' ? 'text-col_accent01' : ''
        }`}
      >
        {title}
      </div>
    </div>
  </div>
);

const MenuDropdown: React.FC<MenuDropdownProps> = ({
  item,
  className,
  children,
  historyItemClassName,
}) => {
  const { t } = useTranslation();
  const { getPageName } = usePageName();
  const { showDeleteChatModal } = useDeleteHistory();
  const { showRenameChatModal } = useRenameHistory();
  const { setHistoryFavoriteStatus } = useFavoriteHistory();
  const isFavorite = item?.favoriteStatus === FAVORITE_STATUS.FAVORITE;

  const trackParams = useMemo(() => {
    return {
      page: getPageName(),
      chat_id: item.chatID,
    };
  }, [item.chatID]);

  const favoriteMenu = isFavorite
    ? {
        key: HISTORY_OPERATE_TYPE.UNFAVORITE,
        label: <MenuItem title={t('batch_unstar')} icon={<HistoryMenuUnfavoriteIcon />} />,
      }
    : {
        key: HISTORY_OPERATE_TYPE.FAVORITE,
        label: <MenuItem title={t('batch_star')} icon={<HistoryMenuFavoriteIcon />} />,
      };

  const menuItems = [
    favoriteMenu,
    {
      key: HISTORY_OPERATE_TYPE.RENAME,
      label: <MenuItem title={t('rename_popup')} icon={<HistoryMenuRenameIcon />} />,
    },
    {
      key: HISTORY_OPERATE_TYPE.DELETE,
      label: <MenuItem title={t('delete_popup')} icon={<HistoryMenuDeleteIcon />} type='warning' />,
    },
  ];

  const handleMenuClick = (info: any) => {
    info.domEvent.stopPropagation();
    switch (info.key) {
      case HISTORY_OPERATE_TYPE.DELETE:
        showDeleteChatModal([item.chatID], trackParams);
        break;
      case HISTORY_OPERATE_TYPE.RENAME:
        showRenameChatModal(item.chatID, trackParams);
        break;
      case HISTORY_OPERATE_TYPE.FAVORITE:
        setHistoryFavoriteStatus([item.chatID], FAVORITE_STATUS.FAVORITE);
        break;
      case HISTORY_OPERATE_TYPE.UNFAVORITE:
        setHistoryFavoriteStatus([item.chatID], FAVORITE_STATUS.UNFAVORITE);
        break;
      default:
        break;
    }
    trackOpClick(info.key, trackParams);
  };

  return (
    <Dropdown
      overlayClassName='history-item-dropdown'
      menu={{ items: menuItems, onClick: handleMenuClick }}
      trigger={['click', 'contextMenu']}
      // @ts-expect-error
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div
        className={classNames(
          'history-item group/history  hover:text-col_text01 relative  flex h-[36px] cursor-pointer items-center justify-between rounded-[12px] text-[14px]  md:w-full ',
          historyItemClassName,
        )}
      >
        {children}
        <div className={`h-5 w-5 cursor-pointer rounded ${className} p-[1px]`}>
          <HistoryItemMoreIcon />
        </div>
      </div>
    </Dropdown>
  );
};

export default MenuDropdown;
